import React, { createContext, FC, ReactElement, ReactNode, useContext, useEffect, useState } from 'react'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'
import { ALL_VALUE, getTelephonyServer } from '../../Common/API/ItemsGetters'
import { ReactState } from '../../Common/Contexts'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { IndividualTelItem } from '../../Common/Types/Types'
import { getServerTypeConfig, ServerTypeFields } from '../Config/ServerTypeConfig'
import Demo from '../Config/ServerTypeConfig/Demo.json'
import { AgentFilters } from '../Types/Types'

export type ConfigureAgentsContextType = {
    viewOnly: boolean
    selectedAgent?: string
    filters: AgentFilters
    serverTypeConfig: ServerTypeFields
    licenceType: ReactState<Nullable<ReportingAccessLevels>>
}

type Props = {
    children: ReactNode
    viewOnly: boolean
    selectedAgent?: string
    filters: AgentFilters
}

export const ConfigureAgentsContextProvider: FC<Props> = ({
    children,
    viewOnly,
    selectedAgent,
    filters
}: Props): ReactElement => {
    const [serverTypeConfig, setServerTypeConfig]: ReactState<Nullable<ServerTypeFields>> =
        useState<Nullable<ServerTypeFields>>()
    const [licenceType, setLicenceType]: ReactState<Nullable<ReportingAccessLevels>> =
        useState<Nullable<ReportingAccessLevels>>(null)

    const { displayError }: MessageContextType = useContext(MessageContext)

    useEffect((): void => {
        const { telSysID }: AgentFilters = filters

        getTelephonyServer(telSysID)
            .then((tel: Nullable<IndividualTelItem>): void => {
                setServerTypeConfig(getServerTypeConfig(tel?.TelSysType))

                if (isStringNotEmpty(tel?.LicensedForRptUserType)) {
                    setLicenceType((tel?.LicensedForRptUserType ?? ReportingAccessLevels.NONE) as ReportingAccessLevels)
                }
            })
            .catch(displayError)
    }, [])

    return (
        <ConfigureAgentsContext.Provider
            value={{
                viewOnly,
                selectedAgent,
                filters,
                serverTypeConfig: serverTypeConfig ?? Demo.fields,
                licenceType: [licenceType, setLicenceType]
            }}
        >
            {children}
        </ConfigureAgentsContext.Provider>
    )
}

export const ConfigureAgentsContext = createContext<ConfigureAgentsContextType>({
    viewOnly: false,
    selectedAgent: undefined,
    filters: {
        cmeID: DEFAULT_CME_ID,
        telSysID: ALL_VALUE,
        partitionID: ALL_VALUE
    },
    serverTypeConfig: Demo.fields,
    licenceType: [
        null,
        (): void => {
            // eslint-disable-next-line no-console
            console.debug('Configure Agent Context is not initialised.')
        }
    ]
})
